import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Typography,
} from "@material-ui/core";
import { DateTimePicker } from "@material-ui/pickers";
import clsx from "clsx";
import moment from "moment-timezone";

// Hooks
import useCurrentFacility from "../../../hooks/useCurrentFacility";
import useCurrentFacilityTimezone from "../../../hooks/useCurrentFacilityTimezone";
import useContextDevices from "../../../hooks/useContextDevices";
import useCancellationToken from "../../../hooks/useCancellationToken";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import useLPRScoringQueue from "../useLPRScoringQueue";

// Components
import LPRScoringCard from "../LPRScoringCard";
import LPRScoringControls from "../LPRScoringControls";

import { useStyles } from "./index.styles";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  variant: "menu",
  getContentAnchorEl: null,
};

/**
 * The Camera Scoring tab container (AO-33366). Owns the device multi-select
 * (entry/exit devices only, clearable, facility-scoped) and date-range filters,
 * and wires the useLPRScoringQueue hook to the review card and controls.
 */
const LPRScoringContainer = () => {
  const classes = useStyles();
  const { facilityID } = useCurrentFacility();
  const { timeZone } = useCurrentFacilityTimezone();
  const enqueueSnackbar = useEnqueueSnackbar();

  const scopeAwareFacilityID = useSelector(
    (state) => state.entityScope?.facilityGroupId || facilityID,
    shallowEqual
  );
  const selectedFacilities = useSelector(
    (state) => state.entityScope?.selected ?? [],
    shallowEqual
  );

  const contextDevices = useContextDevices(true);
  const availableDevices = useMemo(() => {
    if (!contextDevices || contextDevices.length === 0) return [];
    const devices = [];
    for (const facility of contextDevices) {
      if (!facility.devices) continue;
      for (const device of facility.devices) {
        if (device.deviceMode === "Entry" || device.deviceMode === "Exit") {
          devices.push({ deviceId: device.entityid, deviceName: device.name });
        }
      }
    }
    return devices;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(contextDevices)]);

  const [selectedDevices, setSelectedDevices] = useState([]);
  const [startDateTime, setStartDateTime] = useState(() =>
    moment.tz(timeZone || moment.tz.guess()).startOf("day")
  );
  const [endDateTime, setEndDateTime] = useState(() =>
    moment.tz(timeZone || moment.tz.guess()).startOf("day").hours(23).minutes(59)
  );

  const {
    currentRead,
    currentIndex,
    savedScoreForCurrent,
    remaining,
    scored,
    isLoading,
    error,
    hasLoaded,
    hasNext,
    isCurrentScored,
    loadQueue,
    goPrevious,
    skip,
    saveAndNext,
  } = useLPRScoringQueue();

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (timeZone) {
      setStartDateTime(moment.tz(timeZone).startOf("day"));
      setEndDateTime(moment.tz(timeZone).startOf("day").hours(23).minutes(59));
    }
  }, [timeZone]);

  useEffect(() => {
    setSelectedDevices([]);
  }, [selectedFacilities, scopeAwareFacilityID]);

  const handleDeviceChange = (e) => {
    const value = e.target.value;
    if (value.includes(undefined)) return;
    setSelectedDevices(value);
  };

  const clearDevices = useCallback(() => setSelectedDevices([]), []);

  const getDeviceName = (deviceId) => {
    const device = availableDevices.find((d) => d.deviceId === deviceId);
    return device ? device.deviceName : deviceId;
  };

  const effectiveDeviceIDs = useMemo(
    () =>
      selectedDevices.length > 0
        ? selectedDevices
        : availableDevices.map((d) => d.deviceId),
    [selectedDevices, availableDevices]
  );

  const datesValid =
    moment.isMoment(startDateTime) &&
    moment.isMoment(endDateTime) &&
    startDateTime.isValid() &&
    endDateTime.isValid() &&
    startDateTime.isBefore(endDateTime);

  const canLoad = effectiveDeviceIDs.length > 0 && datesValid && !isLoading;

  const executeLoad = useCancellationToken({
    func: async ({ cancelToken }) => {
      await loadQueue(
        {
          deviceIDs: effectiveDeviceIDs,
          startDateTime: startDateTime.clone().utc().format(),
          endDateTime: endDateTime.clone().utc().format(),
        },
        cancelToken
      );
    },
    errHandleFunc: () => {
      enqueueSnackbar("Failed to load the scoring queue", {
        variant: "error",
        tag: "LPRScoringLoadFailed",
      });
    },
  }).execute;

  const handleSearch = useCallback(() => {
    if (!canLoad) return;
    executeLoad();
  }, [canLoad, executeLoad]);

  const handleSaveNext = useCallback(
    async (score) => {
      setIsSaving(true);
      try {
        await saveAndNext(score);
      } catch (err) {
        enqueueSnackbar("Failed to save the score", {
          variant: "error",
          tag: "LPRScoringSaveFailed",
        });
      } finally {
        setIsSaving(false);
      }
    },
    [saveAndNext, enqueueSnackbar]
  );

  useEffect(() => {
    if (error) {
      enqueueSnackbar("Failed to load the scoring queue", {
        variant: "error",
        tag: "LPRScoringLoadFailed",
      });
    }
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxDate = moment.tz(timeZone || moment.tz.guess()).endOf("day");

  return (
    <Box className={classes.column}>
      <FormControl variant="outlined" fullWidth size="small" className={classes.filterField}>
        <InputLabel shrink id="lpr-scoring-device-label">
          Device Filter
        </InputLabel>
        <Select
          labelId="lpr-scoring-device-label"
          multiple
          displayEmpty
          value={selectedDevices}
          onChange={handleDeviceChange}
          input={<OutlinedInput notched label="Device Filter" />}
          renderValue={(selected) => {
            if (selected.length === 0) {
              return <span className={classes.devicePlaceholder}>Device Filter</span>;
            }
            return selected.map((id) => getDeviceName(id)).join(", ");
          }}
          MenuProps={MenuProps}
          data-testid="lpr-scoring-device-select"
        >
          {selectedDevices.length > 0 && (
            <MenuItem onClick={clearDevices} data-testid="lpr-scoring-clear-devices">
              <ListItemText primary="Clear selection" />
            </MenuItem>
          )}
          {availableDevices.map((device) => (
            <MenuItem key={device.deviceId} value={device.deviceId}>
              <Checkbox checked={selectedDevices.indexOf(device.deviceId) > -1} />
              <ListItemText primary={device.deviceName} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <DateTimePicker
        label="From"
        value={startDateTime}
        onChange={setStartDateTime}
        inputVariant="outlined"
        format="MM/DD/YYYY hh:mm A"
        fullWidth
        size="small"
        autoOk
        maxDate={maxDate}
        className={clsx(classes.filterField, "lpr-scoring-start-date-picker")}
        data-testid="lpr-scoring-start-date"
      />
      <DateTimePicker
        label="To"
        value={endDateTime}
        onChange={setEndDateTime}
        inputVariant="outlined"
        format="MM/DD/YYYY hh:mm A"
        fullWidth
        size="small"
        autoOk
        maxDate={maxDate}
        className={clsx(classes.filterField, "lpr-scoring-end-date-picker")}
        data-testid="lpr-scoring-end-date"
      />

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleSearch}
        disabled={!canLoad}
        className="lpr-scoring-search-button"
        data-testid="lpr-scoring-search"
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : "Search"}
      </Button>

      <Paper variant="outlined" className={classes.reviewCard}>
        {isLoading && (
          <Box className={classes.stateBox} data-testid="lpr-scoring-loading">
            <CircularProgress size={28} />
          </Box>
        )}

        {!isLoading && !hasLoaded && (
          <Box className={classes.stateBox}>
            <Typography
              variant="body2"
              color="textSecondary"
              className="lpr-scoring-prompt"
            >
              Choose a device and date range, then press Search to begin scoring.
            </Typography>
          </Box>
        )}

        {!isLoading && hasLoaded && !currentRead && (
          <Box className={classes.stateBox}>
            <Typography variant="subtitle1" gutterBottom>
              No reads to score
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Every read in this range has been scored, or none have images.
            </Typography>
          </Box>
        )}

        {!isLoading && currentRead && (
          <>
            <LPRScoringCard read={currentRead} />
            <LPRScoringControls
              lprId={currentRead.lprId}
              savedScore={savedScoreForCurrent}
              remaining={remaining}
              scored={scored}
              isFirst={currentIndex === 0}
              isSaving={isSaving}
              hasCurrentRead={Boolean(currentRead)}
              canSkip={hasNext}
              canSave={hasNext || !isCurrentScored}
              onPrevious={goPrevious}
              onSkip={skip}
              onSaveNext={handleSaveNext}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default LPRScoringContainer;
